import { Controller } from "@hotwired/stimulus";

// Larger sizes for the lightbox, derived from each thumbnail's Shopify CDN
// URL by changing its `width` parameter (the CDN never upscales).
const LIGHTBOX_WIDTHS = [1080, 1600, 2400];

// Event photo gallery: a grid revealed one batch at a time, and a lightbox
// built on a native <dialog>. Photos past the first batch are rendered with
// the `hidden` attribute, so their lazy images load only once revealed.
export default class extends Controller {
  static targets = ["item", "more", "dialog", "image", "counter"];
  static values = { batch: { type: Number, default: 48 } };

  connect() {
    this.close = this.close.bind(this);
    document.addEventListener("turbo:before-cache", this.close);
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.close);
  }

  showMore() {
    const hidden = this.itemTargets.filter((item) => item.hidden);
    hidden.slice(0, this.batchValue).forEach((item) => (item.hidden = false));
    this.moreTarget.hidden = hidden.length <= this.batchValue;
  }

  open({ params: { index } }) {
    this.show(index);
    this.dialogTarget.showModal();
  }

  close() {
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close();
  }

  next() {
    this.show(this.index + 1);
  }

  previous() {
    this.show(this.index - 1);
  }

  navigate(event) {
    if (event.key === "ArrowRight") this.next();
    if (event.key === "ArrowLeft") this.previous();
  }

  // A click on the backdrop targets the <dialog> element itself.
  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.close();
  }

  touchStart(event) {
    this.touchX = event.changedTouches[0].clientX;
  }

  touchEnd(event) {
    const distance = event.changedTouches[0].clientX - this.touchX;
    if (Math.abs(distance) < 50) return;

    if (distance < 0) this.next();
    else this.previous();
  }

  show(index) {
    const count = this.itemTargets.length;
    this.index = (index + count) % count;

    const thumbnail = this.#thumbnail(this.index);
    this.imageTarget.width = thumbnail.getAttribute("width");
    this.imageTarget.height = thumbnail.getAttribute("height");
    this.imageTarget.sizes = this.#sizes(thumbnail);
    this.imageTarget.srcset = this.#srcset(thumbnail);
    this.imageTarget.src = this.#resize(thumbnail.src, 1600);
    this.imageTarget.alt = thumbnail.alt;
    this.counterTarget.textContent = `${this.index + 1} / ${count}`;

    this.#preload(this.#thumbnail((this.index + 1) % count));
  }

  #thumbnail(index) {
    return this.itemTargets[index].querySelector("img");
  }

  #resize(src, width) {
    const url = new URL(src, window.location.href);
    url.searchParams.set("width", width);
    return url.toString();
  }

  // The photo is fitted to the viewport, so portrait photos are limited by
  // the height: "100vw" would fetch a size far larger than what is shown.
  #sizes(thumbnail) {
    const ratio = thumbnail.getAttribute("width") / thumbnail.getAttribute("height");
    return `${Math.round(Math.min(window.innerWidth, window.innerHeight * ratio))}px`;
  }

  #srcset(thumbnail) {
    return LIGHTBOX_WIDTHS.map((width) => `${this.#resize(thumbnail.src, width)} ${width}w`).join(", ");
  }

  #preload(thumbnail) {
    const image = new Image();
    image.sizes = this.#sizes(thumbnail);
    image.srcset = this.#srcset(thumbnail);
  }
}
